import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBoxesStacked,
  faCheck,
  faReceipt,
  faRotateLeft,
  faStore,
  faTruck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import Loader from '../Loader/Loader';
import './MisVentas.css';

function MisVentas() {
  const [ventas, setVentas] = useState([]);
  const [summary, setSummary] = useState({ pedidos: 0, productos: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const formatoPrecio = (valor) => new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency: 'ARS',
  }).format(Number(valor || 0));

  const tiendasConVentas = useMemo(() => {
    const tiendas = new Set();
    ventas.forEach((venta) => {
      (venta.items || []).forEach((item) => tiendas.add(item.Idtienda));
    });
    return tiendas.size;
  }, [ventas]);

  useEffect(() => {
    const cargarVentas = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        const response = await fetch('/api/mis-ventas', {
          method: 'GET',
          credentials: 'include',
        });

        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'No pudimos cargar tus ventas.');
        }

        setVentas(Array.isArray(data.ventas) ? data.ventas : []);
        setSummary(data.summary || { pedidos: 0, productos: 0, total: 0 });
      } catch (error) {
        console.error('Error al obtener ventas:', error);
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    cargarVentas();
  }, []);

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Fecha no informada';
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(fecha));
  };

  const etiquetaEstado = (estado) => ({
    pendiente_pago: 'pendiente de pago',
    confirmado: 'confirmado',
    entregado: 'entregado',
    cancelado: 'cancelado',
  }[estado] || estado || 'pendiente');

  const accionesPorEstado = (estado) => {
    if (estado === 'entregado') {
      return [
        { estado: 'confirmado', label: 'Reabrir', icon: faRotateLeft, secondary: true },
      ];
    }
    if (estado === 'cancelado') {
      return [
        { estado: 'confirmado', label: 'Reactivar', icon: faRotateLeft, secondary: true },
      ];
    }
    if (estado === 'confirmado') {
      return [
        { estado: 'entregado', label: 'Marcar entregado', icon: faTruck },
        { estado: 'cancelado', label: 'Cancelar', icon: faXmark, danger: true },
      ];
    }
    return [
      { estado: 'confirmado', label: 'Confirmar', icon: faCheck },
      { estado: 'cancelado', label: 'Cancelar', icon: faXmark, danger: true },
    ];
  };

  const actualizarEstado = async (ventaId, estado) => {
    try {
      setUpdatingId(ventaId);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await fetch(`/api/ventas/${ventaId}/estado`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ estado }),
      });

      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'No pudimos actualizar el estado.');
      }

      setVentas((actuales) => actuales.map((venta) => (
        venta._id === ventaId ? { ...venta, estado, updated_at: data.compra?.updated_at } : venta
      )));
      setSuccessMessage('Estado actualizado.');
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      setErrorMessage(error.message);
    } finally {
      setUpdatingId('');
    }
  };

  if (loading) {
    return (
      <section className="mis-ventas-panel">
        <div className="mis-ventas-loading">
          <Loader />
          <p>Cargando tus ventas</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mis-ventas-panel">
      <div className="mis-ventas-header">
        <div>
          <p>Vendedor</p>
          <h2>Mis Ventas</h2>
        </div>
        <Link to="/cuenta#MisPublicaciones" className="mis-ventas-primary">
          Ver publicaciones
          <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>

      {errorMessage && <p className="mis-ventas-message error">{errorMessage}</p>}
      {successMessage && <p className="mis-ventas-message success">{successMessage}</p>}

      <div className="mis-ventas-summary">
        <article>
          <span><FontAwesomeIcon icon={faReceipt} /></span>
          <small>Pedidos recibidos</small>
          <strong>{summary.pedidos || 0}</strong>
        </article>
        <article>
          <span><FontAwesomeIcon icon={faBoxesStacked} /></span>
          <small>Productos vendidos</small>
          <strong>{summary.productos || 0}</strong>
        </article>
        <article>
          <span><FontAwesomeIcon icon={faStore} /></span>
          <small>Tiendas con ventas</small>
          <strong>{tiendasConVentas}</strong>
        </article>
      </div>

      <div className="mis-ventas-total">
        <span>Total vendido</span>
        <strong>{formatoPrecio(summary.total)}</strong>
      </div>

      {ventas.length > 0 ? (
        <div className="mis-ventas-orders">
          {ventas.map((venta) => (
            <article className="mis-ventas-order" key={venta._id}>
              <div className="mis-ventas-order-head">
                <div>
                  <strong>Pedido #{venta._id.slice(-8).toUpperCase()}</strong>
                  <p>{formatearFecha(venta.created_at)}</p>
                </div>
                <span>{etiquetaEstado(venta.estado)}</span>
              </div>

              <div className="mis-ventas-items">
                {(venta.items || []).map((item, index) => (
                  <article className="mis-ventas-item" key={`${venta._id}-${item.avisoid}-${index}`}>
                    <Link to={`/aviso/${item.avisoid}`} className="mis-ventas-item-media" aria-label={item.titulo}>
                      {item.imagen ? <img src={item.imagen} alt={item.titulo} /> : <span>{item.avisoid}</span>}
                    </Link>
                    <div>
                      <Link to={`/aviso/${item.avisoid}`} className="mis-ventas-item-title">{item.titulo}</Link>
                      <p>{item.Tienda} · {item.cant} unidad{item.cant === 1 ? '' : 'es'}</p>
                    </div>
                    <strong>{formatoPrecio(item.subtotal)}</strong>
                  </article>
                ))}
              </div>

              <div className="mis-ventas-order-total">
                <span>{venta.total_cantidad} producto{venta.total_cantidad === 1 ? '' : 's'}</span>
                <strong>{formatoPrecio(venta.total_precio)}</strong>
              </div>

              <div className="mis-ventas-actions">
                {accionesPorEstado(venta.estado).map((accion) => (
                  <button
                    key={`${venta._id}-${accion.estado}`}
                    type="button"
                    className={[
                      'mis-ventas-action',
                      accion.secondary ? 'secondary' : '',
                      accion.danger ? 'danger' : '',
                    ].filter(Boolean).join(' ')}
                    disabled={updatingId === venta._id}
                    onClick={() => actualizarEstado(venta._id, accion.estado)}
                  >
                    <FontAwesomeIcon icon={accion.icon} />
                    {updatingId === venta._id ? 'Actualizando...' : accion.label}
                  </button>
                ))}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="mis-ventas-empty">
          <FontAwesomeIcon icon={faStore} />
          <h3>Todavia no tenes ventas registradas</h3>
          <p>Cuando alguien confirme una compra con productos de tus tiendas, vas a verla aca.</p>
          <Link to="/cuenta#crear-aviso">Publicar producto</Link>
        </div>
      )}
    </section>
  );
}

export default MisVentas;
